import StudioAPI from "../api/StudioAPI";
import {fetchProject} from "./projectsActions";
import {
  createProductBegin,
  createProductSuccess,
  createProductFailure,
  updateProductBegin,
  updateProductSuccess,
  updateProductFailure,
  deleteProductBegin,
  deleteProductFailure,
  deleteProductSuccess,
  duplicateProductBegin,
  duplicateProductSuccess,
  duplicateProductFailure,
  fetchProductStatusFailure,
  fetchProductStatusSuccess,
  importFileBegin,
  importFileSuccess,
  importFileFailure,
  importFileProgressStatus
} from "../actions/products";
import {GenericThunkAction, GenericThunkDispatch, GenericThunkGetState} from "../types/Types";
import {ErrorResponse, ProductData} from "../types/StudioAPITypes";
import PollManager from "../utils/PollingManager";
import {
  IMPORT_POLL_INTERVAL,
  PRODUCT_STATUS,
  MOCK_IMPORT_FILE_PROGRESS_TIME_THRESHOLD,
  MOCK_IMPORT_FILE_PROGRESS_INTERVAL
} from "../utils/constants";
import GenericErrorContainer from "../containers/GenericErrorContainer";

let FILE_IMPORT_INTERVAL_REF = <any> null;

const api = StudioAPI.getInstance();

export function deleteProduct(projectId: string, productId: string): GenericThunkAction<Promise<{} | void>> {
  return (dispatch: GenericThunkDispatch, getState: GenericThunkGetState) => {
    dispatch(deleteProductBegin());

    return api.deleteProduct(projectId, productId).then((response: {data:{}}) => {

      // Refresh page with latest projects after delete
      dispatch(fetchProject(projectId));

      dispatch(deleteProductSuccess(response.data))

      return response;
    }, (error: ErrorResponse) => {
      dispatch(deleteProductFailure(error));
    })
  }
}

export function addProduct(projectId: string, name: string): GenericThunkAction<Promise<{} | void>> {
  return (dispatch: GenericThunkDispatch, getState: GenericThunkGetState) => {
    dispatch(createProductBegin());

    return api.addProduct(projectId, {
      name: name
    }).then((response: {data:any}) => {
      dispatch(createProductSuccess(response.data));

      return response.data;
    }, (error: ErrorResponse) => {
      dispatch(createProductFailure(error));
    })
  }
}

export function updateProduct(projectId: string, productId: string, data: ProductData): GenericThunkAction<Promise<{} | void>> {
  return (dispatch: GenericThunkDispatch, getState: GenericThunkGetState) => {
    dispatch(updateProductBegin());

    return api.updateProduct(projectId, productId, data).then((response: {data:{}}) => {
      dispatch(updateProductSuccess(response.data));

      return response.data;
    }, (error: ErrorResponse) => {
      dispatch(updateProductFailure(error));
    })
  }
}

export function fetchProductStatus(projectId: string, productId: string) {
  return (dispatch: GenericThunkDispatch, getState: GenericThunkGetState) => {
    let productStatus = PRODUCT_STATUS;
    return api.fetchProductStatus(projectId, productId).then( (response: {data: any}) => {
      let inProgress = response.data.productStatus !== productStatus.ACTIVE && response.data.productStatus !== productStatus.CONVERSION_FAILED;
      let progressPct = mockProductsProgressStatus(response.data.id);
      if (!inProgress) {
        PollManager.remove(PollManager.createKey(projectId, response.data.id));
        localStorage.removeItem(getLocalStoreKeyForProgress(response.data.id));
        progressPct = 100;
      }
      dispatch(fetchProductStatusSuccess(response.data, inProgress, progressPct));
      }),
      (error: ErrorResponse) => {
        // Something Went wrong. Kill the timeout
        PollManager.remove(PollManager.createKey(projectId, productId));
        localStorage.removeItem(getLocalStoreKeyForProgress(productId));
        dispatch(fetchProductStatusFailure(error));
      }
  }
}

export function duplicateProduct(projectId: string, data: ProductData): GenericThunkAction<Promise<{} | void>> {
  return (dispatch: GenericThunkDispatch, getState: GenericThunkGetState) => {
    dispatch(duplicateProductBegin())
    return api.duplicateProduct(projectId, data).then( (response: {data:any}) => {
        PollManager.add(PollManager.createKey(projectId, response.data.id), () => {
          return dispatch(fetchProductStatus(projectId, response.data.id));
        }, IMPORT_POLL_INTERVAL);
        dispatch(duplicateProductSuccess(response.data));
        return response.data
        },
          (error: ErrorResponse) => {
          dispatch(duplicateProductFailure(error));
        })
  }
}

export function importFile(projectId: string, file: File): GenericThunkAction<Promise<{} | void>> {
  return (dispatch: GenericThunkDispatch, getState: GenericThunkGetState) => {
    dispatch(importFileBegin());
    var importStartTime = new Date().getTime();
    return api.importFile(projectId, file, (e: any) => calculateProgressPercentage(e, dispatch, importStartTime))
      .then( (response: {data:any}) => {
          PollManager.add(PollManager.createKey(projectId, response.data.id), () => {
          return dispatch(fetchProductStatus(projectId, response.data.id));
        }, IMPORT_POLL_INTERVAL);
          mockGcsTransfer(dispatch, true);
          return response
      },
      (error: ErrorResponse) => {
          mockGcsTransfer(dispatch, true);
          dispatch(importFileFailure(error));
        return Promise.reject(error);
      }).then( (response: any ) => {
        return mockProgressIfFastUpload(response, importStartTime, dispatch);
      }).then( (response: any) => {
          dispatch(importFileSuccess(response.data));
        return response.data
      })
  }
}

function calculateProgressPercentage(e: any, dispatch: GenericThunkDispatch, importStartTime: number) {
  // This represents the percent complete for the first step of a file import. When this step is complete
  // It will fill up 50% of the progress bar
    let shouldMockIndicator = isLoadingToFastForIndicator(e, importStartTime);
  if (e.loaded !== e.total && !shouldMockIndicator) {
    dispatch(importFileProgressStatus(Math.floor(((e.loaded)/e.total)*50)));
  } else if (e.loaded === e.total && !shouldMockIndicator) {
    mockGcsTransfer(dispatch, false);
  }
}

function isLoadingToFastForIndicator(e: any, importStartTime: number) {
  // Estimated that if loaded more than 75% before the time threshold it will load to fast and indicator will be choppy
  return ((e.loaded/e.total)*100 > 50) &&
    (new Date().getTime() - importStartTime < MOCK_IMPORT_FILE_PROGRESS_TIME_THRESHOLD);
}

function mockGcsTransfer(dispatch: GenericThunkDispatch, kill: boolean) {
  let pct = 50;
  if (kill) {
    clearInterval(FILE_IMPORT_INTERVAL_REF);
    dispatch(importFileProgressStatus(100));
  } else {
      FILE_IMPORT_INTERVAL_REF = setInterval(() => {
          pct = pct + ((100-pct)/6);
          dispatch(importFileProgressStatus(pct));
      }, 5000)
  }
}

function mockProgressIfFastUpload(response: any, importStartTime: number, dispatch: GenericThunkDispatch): Promise<{} | void> {
  return new Promise(function(resolve, reject) {
    if (new Date().getTime() - importStartTime < MOCK_IMPORT_FILE_PROGRESS_TIME_THRESHOLD) {
      var pct = 0;
      var interval = setInterval(() => {
        pct += 33;
        if (pct < 100) {
          dispatch(importFileProgressStatus(pct));
        } else {
          dispatch(importFileProgressStatus(pct));
          clearInterval(interval);
          return resolve(response);
        }
      }, MOCK_IMPORT_FILE_PROGRESS_INTERVAL)
    } else {
      return resolve(response);
    }
  });
}

const mockProductsProgressStatus = (productId: string) => {
  // TODO This is temporary until progress is calculated form the server.
  /*
   * Function increments a mocked progress percentage for an a product conversion. Saving cache to client so that
   * the progress is saved on refresh or navigating out of the product page. If the progress is incremented to 95%
   * before the actual success of a conversion then the indicator is stalled until a success
   *
   */
  var key = getLocalStoreKeyForProgress(productId);
  var progressVal = parseInt(localStorage.getItem(key) || "0" );

  if (progressVal < 95) {
    progressVal += 5;
    localStorage.setItem(key, progressVal.toString())
  }
  return progressVal;
};

const getLocalStoreKeyForProgress = (productId: string) => {
  return `STUDIO_PROGRESS_${productId}`;
};

